import { useCallback, useMemo, useState, type FormEvent, type SyntheticEvent } from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import {
    EVENT_THEME_PRESETS,
    EventBannerContent,
    EventModalContent,
    OverlayScrollArea,
    resolveEventSurface,
    resolveThemePreset,
    useDebounce,
    type EventDto,
    type EventImageUrlResolver,
    type EventThemePreset,
    type RichEditorImageUploadHandler
} from '@odo/shared-ui';
import { useCreateEvent, useEventEmailPreview, useUpdateEvent } from '../../hooks/useEvents';
import { useToast } from '../../providers/ToastProvider';
import { resolveEventImageUrls, uploadEventImage } from '../../services/eventImages';
import { apiErrorMessage, uiErrorMessage } from '../../utils/apiError';
import { AppearanceSection } from './AppearanceSection';
import { AudienceSection } from './AudienceSection';
import { BasicsSection } from './BasicsSection';
import { ContentSection } from './ContentSection';
import {
    draftFromEvent,
    draftFromEventForDuplicate,
    draftToCreateInput,
    draftToPreviewDto,
    draftToUpdateInput,
    draftWithKind,
    emptyEventDraft,
    type EventDraft
} from './eventDraft';
import { isAnnouncementKind, toEventKind } from './eventKind';
import { EventFormSection } from './EventFormSection';
import {
    expandSectionsWithErrors,
    sectionHasError,
    type EventFormSectionId
} from './eventFormSections';
import {
    appearanceSectionSummary,
    audienceSectionSummary,
    basicsSectionSummary,
    contentSectionSummary,
    linksSectionSummary
} from './eventFormSummaries';
import { LinksSection } from './LinksSection';
import { validateEventDraft } from './eventValidation';
import { SECTION_TITLE_CLASS, type FieldSetter, type SectionProps } from './eventFormShared';

const THEME_PRESET_KEYS = Object.keys(EVENT_THEME_PRESETS);
const DEFAULT_THEME_PRESET = THEME_PRESET_KEYS[0] ?? 'yellow';

interface PreviewPaneProps {
    event: EventDto;
    isAnnouncement: boolean;
    resolveImageUrls: EventImageUrlResolver;
}

// The email template renders at a fixed width; 520px matches the old
// fixed-height iframe so the preview footprint in the form is unchanged.
const EMAIL_PREVIEW_MAX_HEIGHT_PX = 520;

/** The confirmation email, rendered by the server's own template - the only
 *  place `badgeLabel` is visible. `sandbox="allow-same-origin"` lets us
 *  measure the document to size the iframe to its content (so the shared
 *  overlay scrollbar outside the iframe does the scrolling instead of the
 *  iframe's native one); without `allow-scripts` the admin-authored HTML
 *  still cannot run any code. */
function EmailPreview({ event }: { event: EventDto }) {
    const [frameHeight, setFrameHeight] = useState(EMAIL_PREVIEW_MAX_HEIGHT_PX);
    const handleFrameLoad = useCallback((e: SyntheticEvent<HTMLIFrameElement>) => {
        const doc = e.currentTarget.contentDocument;
        const height = doc?.documentElement?.scrollHeight ?? 0;
        if (height > 0) setFrameHeight(height);
    }, []);

    const input = useMemo(
        () => ({
            kind: toEventKind(event.kind),
            title: event.title,
            topic: event.topic,
            badgeLabel: event.badgeLabel,
            subtitle: event.subtitle,
            body: event.body,
            bodyDoc: event.bodyDoc,
            themePreset: event.themePreset,
            startsAt: event.startsAt,
            googleCalendarUrl: event.googleCalendarUrl,
            icsUrl: event.icsUrl
        }),
        [event]
    );
    const debounced = useDebounce(input, 500);
    const { preview, isLoading, isError } = useEventEmailPreview(debounced);

    return (
        <div className="rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            {isError ? (
                <p className="text-sm text-red-500 p-5">Couldn't render the email preview.</p>
            ) : preview ? (
                <>
                    <p className="px-4 py-2.5 border-b border-gray-200 text-xs text-gray-500 truncate">
                        <span className="font-semibold text-gray-700">Subject</span>{' '}
                        {preview.subject}
                    </p>
                    {/* max-h must stay a literal class (Tailwind JIT) and match
                        EMAIL_PREVIEW_MAX_HEIGHT_PX. */}
                    <OverlayScrollArea contentClassName="max-h-[520px]" trackInsetPx={8}>
                        <iframe
                            title="Event confirmation email preview"
                            srcDoc={preview.html}
                            sandbox="allow-same-origin"
                            scrolling="no"
                            onLoad={handleFrameLoad}
                            className="block w-full bg-white"
                            style={{ height: frameHeight }}
                        />
                    </OverlayScrollArea>
                </>
            ) : (
                <div className="flex items-center justify-center gap-2 h-[120px] text-gray-400">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">{isLoading ? 'Rendering email…' : 'Waiting…'}</span>
                </div>
            )}
        </div>
    );
}

/** Non-interactive replica of the banner's action (mirrors EventBanner's
 *  `signUpButton`). A `<span>`, not a `<button>` - this sits inside the form. */
function PreviewActionButton({ event, theme }: { event: EventDto; theme: EventThemePreset }) {
    return (
        <span
            className={`${theme.accent} ${theme.accentText} inline-flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-semibold shadow-md cursor-default`}
        >
            <CalendarDaysIcon className="w-4 h-4" />
            {event.actionLabel || 'RSVP'}
        </span>
    );
}

/** Renders the exact `@odo/shared-ui` components practitioners see, fed from
 *  in-progress form state - the whole reason those renderers are shared.
 *  Full height beside the form on `lg+` (each side scrolls independently, so
 *  the banner/pop-up never scroll out of view while editing); below `lg` it
 *  drops to a plain stacked half rather than squashing the field column down
 *  to nothing. */
function PreviewPane({ event, isAnnouncement, resolveImageUrls }: PreviewPaneProps) {
    // EventBanner applies the theme's bg/text on its wrapper, not the content -
    // without it every preview rendered on white regardless of theme.
    const theme = resolveThemePreset(event.themePreset);
    // The form's own modal-chrome title (EventModalContent renders only the
    // body) - must resolve the same override the real chrome does.
    const modalTitle = resolveEventSurface(event, 'modal').title;

    return (
        <div className="w-full h-1/2 lg:h-auto lg:w-[70%] lg:min-w-[560px] shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
            <OverlayScrollArea className="flex-1 min-h-0" contentClassName="p-6" trackInsetPx={12}>
                <div className="flex flex-col gap-6">
                    <span className={SECTION_TITLE_CLASS}>Live preview</span>
                    <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2">Dashboard banner</p>
                        {/* Same wrapper/layout as EventBanner, including the action
                            `EventBannerContent` only reserves space for (`pr-44`). */}
                        <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                            <div
                                className={`${theme.bg} ${theme.bannerBorder} ${theme.text} w-full px-6 py-3`}
                            >
                                <div className="relative">
                                    <EventBannerContent
                                        event={event}
                                        reserveActionSpace={!isAnnouncement}
                                    />
                                    {!isAnnouncement && (
                                        <div className="absolute inset-y-0 right-0 flex items-center gap-3">
                                            <PreviewActionButton event={event} theme={theme} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-semibold text-gray-500 mb-2">
                            {isAnnouncement ? 'Announcement pop-up' : 'Sign-up modal'}
                        </p>
                        <div className="rounded-xl border border-gray-200 shadow-sm bg-white p-5">
                            <h3 className="text-lg font-bold text-gray-900 mb-4">{modalTitle}</h3>
                            <EventModalContent event={event} resolveImageUrls={resolveImageUrls} />
                        </div>
                    </div>
                    {/* No confirmation email is sent for an announcement. */}
                    {!isAnnouncement && (
                        <div>
                            <p className="text-xs font-semibold text-gray-500 mb-2">
                                Confirmation email
                            </p>
                            <EmailPreview event={event} />
                        </div>
                    )}
                </div>
            </OverlayScrollArea>
        </div>
    );
}

interface EventFormProps {
    /** The event being edited, or `null` to create a new one. */
    event: EventDto | null;
    /** Content-only seed for a new event, from the "Duplicate event" action.
     *  Ignored once `event` is set - editing an existing event never duplicates. */
    duplicateFrom?: EventDto | null;
    /** `events.update`. False renders every field read-only and drops the save
     *  button - the API refuses the write regardless, this just avoids offering it. */
    canManage: boolean;
    /** Where to go once the save succeeds - the list, or the saved event. */
    onSaved: (savedId: string) => void;
    /** Leaving without saving. */
    onCancel: () => void;
}

function useSubmitEvent(event: EventDto | null, onSaved: (savedId: string) => void) {
    const { showToast } = useToast();
    const isEdit = Boolean(event);
    const { mutateAsync: createEvent, isPending: isCreating } = useCreateEvent();
    const { mutateAsync: updateEvent, isPending: isUpdating } = useUpdateEvent();

    const submit = async (draft: EventDraft) => {
        try {
            if (isEdit && event) {
                await updateEvent({ id: event.id, patch: draftToUpdateInput(draft) });
                showToast('Event updated', 'success');
                onSaved(event.id);
            } else {
                // The server derives the slug, so the created id only becomes
                // known here - which is what lets the caller navigate to it.
                const created = await createEvent(draftToCreateInput(draft));
                showToast('Event created', 'success');
                onSaved(created.id);
            }
        } catch (error) {
            showToast(
                apiErrorMessage(
                    error,
                    isEdit ? 'Failed to update event' : 'Failed to create event'
                ),
                'error'
            );
        }
    };

    return { submit, isPending: isCreating || isUpdating };
}

type OpenSections = Record<EventFormSectionId, boolean>;

/** Basics and Content are what an author sets on nearly every event, so both
 *  start open on a new draft. Editing an existing event starts with only
 *  Content open - schedule/appearance are usually already right and rarely
 *  revisited, while content is what a later edit is normally about. */
function initialOpenSections(isEdit: boolean): OpenSections {
    return {
        basics: !isEdit,
        content: true,
        // Folded by default on both paths: the overwhelming majority of events
        // go to every practice, which is the summary line's default reading.
        audience: false,
        appearance: false,
        links: false
    };
}

export function EventForm({
    event,
    duplicateFrom = null,
    canManage,
    onSaved,
    onCancel
}: EventFormProps) {
    const isEdit = Boolean(event);

    const [draft, setDraft] = useState<EventDraft>(() => {
        if (event) return draftFromEvent(event);
        if (duplicateFrom) return draftFromEventForDuplicate(duplicateFrom);
        return emptyEventDraft(DEFAULT_THEME_PRESET);
    });
    const [touched, setTouched] = useState(false);
    const [openSections, setOpenSections] = useState<OpenSections>(() =>
        initialOpenSections(isEdit)
    );
    const { submit, isPending } = useSubmitEvent(event, onSaved);

    const errors = useMemo(
        () =>
            validateEventDraft(draft, {
                isCreate: !isEdit,
                knownThemePresets: THEME_PRESET_KEYS
            }),
        [draft, isEdit]
    );

    // Touched on the first edit, not just on submit - the submit button stays
    // disabled while invalid, so a blocked submit would never reveal errors.
    // Stable identity (useCallback): EventPopupSection's mirror-until-touched
    // effect depends on it, and a fresh function every render would re-fire it.
    const set: FieldSetter = useCallback((key, value) => {
        setTouched(true);
        setDraft((prev) => ({ ...prev, [key]: value }));
    }, []);

    const setKind = (kind: string) => {
        setTouched(true);
        setDraft((prev) => draftWithKind(prev, kind, DEFAULT_THEME_PRESET));
    };

    const isAnnouncement = isAnnouncementKind(draft.kind);

    const hasErrors = Object.keys(errors).length > 0;

    const toggleSection = useCallback((id: EventFormSectionId) => {
        setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const handleImageUpload: RichEditorImageUploadHandler = useCallback(async (file) => {
        try {
            const { url, fileId } = await uploadEventImage(file);
            return { url, fileId };
        } catch (error) {
            throw new Error(uiErrorMessage(error, 'Failed to upload image.'));
        }
    }, []);

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (hasErrors) {
            // A folded section must never be able to hide the field that is
            // blocking submit - open every section an error lives in.
            setOpenSections((prev) => expandSectionsWithErrors(prev, errors));
            return;
        }
        void submit(draft);
    };

    const previewEvent = draftToPreviewDto(draft);
    const sectionProps: SectionProps = {
        draft,
        set,
        setKind,
        errors,
        touched,
        isEdit,
        // A duplicate is a new event whose pop-up fields are already seeded from
        // the source, so it must not be mirrored over.
        seeded: isEdit || Boolean(duplicateFrom),
        canManage,
        isAnnouncement,
        onImageUpload: handleImageUpload
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="flex-1 min-h-0 flex flex-col lg:flex-row gap-6 px-8 pb-6 overflow-hidden"
        >
            <div className="w-full h-1/2 lg:h-auto lg:flex-1 lg:min-w-0 flex flex-col bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* A single `disabled` fieldset rather than a flag threaded through
                    every input - the browser disables every nested control for free. */}
                <OverlayScrollArea
                    className="flex-1 min-h-0"
                    contentClassName="px-8 pt-1 pb-5"
                    trackInsetPx={12}
                >
                    <div className="flex flex-col gap-2">
                        <EventFormSection
                            id="event-form-basics"
                            title="Basics"
                            summary={basicsSectionSummary(draft, isAnnouncement)}
                            hasError={sectionHasError(errors, touched, 'basics')}
                            open={openSections.basics}
                            disabled={!canManage}
                            onToggle={() => toggleSection('basics')}
                        >
                            <BasicsSection {...sectionProps} />
                        </EventFormSection>
                        <EventFormSection
                            id="event-form-content"
                            title="Content"
                            summary={contentSectionSummary(draft)}
                            hasError={sectionHasError(errors, touched, 'content')}
                            open={openSections.content}
                            disabled={!canManage}
                            onToggle={() => toggleSection('content')}
                        >
                            <ContentSection {...sectionProps} />
                        </EventFormSection>
                        <EventFormSection
                            id="event-form-audience"
                            title="Audience"
                            summary={audienceSectionSummary(draft)}
                            hasError={sectionHasError(errors, touched, 'audience')}
                            open={openSections.audience}
                            disabled={!canManage}
                            onToggle={() => toggleSection('audience')}
                        >
                            <AudienceSection {...sectionProps} />
                        </EventFormSection>
                        <EventFormSection
                            id="event-form-appearance"
                            title="Appearance"
                            summary={appearanceSectionSummary(draft)}
                            hasError={sectionHasError(errors, touched, 'appearance')}
                            open={openSections.appearance}
                            disabled={!canManage}
                            onToggle={() => toggleSection('appearance')}
                        >
                            <AppearanceSection {...sectionProps} />
                        </EventFormSection>
                        {/* Dropped whole for an announcement: it has no meeting to
                            join and no add-to-calendar block, so every link here
                            would be dead data - the same gate the old ungrouped
                            Links section used. */}
                        {!isAnnouncement && (
                            <EventFormSection
                                id="event-form-links"
                                title="Links"
                                summary={linksSectionSummary(draft, isEdit)}
                                hasError={sectionHasError(errors, touched, 'links')}
                                open={openSections.links}
                                disabled={!canManage}
                                onToggle={() => toggleSection('links')}
                            >
                                <LinksSection {...sectionProps} />
                            </EventFormSection>
                        )}
                    </div>
                </OverlayScrollArea>

                <div className="px-8 py-4 border-t border-gray-200 flex items-center gap-3 shrink-0">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                    >
                        {canManage ? 'Cancel' : 'Back'}
                    </button>
                    {canManage && (
                        <button
                            type="submit"
                            disabled={isPending || hasErrors}
                            title={hasErrors ? 'Fill in the required fields' : undefined}
                            className="ml-auto bg-black text-white rounded-xl px-6 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors shadow disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create event'}
                        </button>
                    )}
                </div>
            </div>
            {/* Module-level function - already stable, no useCallback wrapper needed. */}
            <PreviewPane
                event={previewEvent}
                isAnnouncement={isAnnouncement}
                resolveImageUrls={resolveEventImageUrls}
            />
        </form>
    );
}
